/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import { createResponseContext,
    initialQueryResponse,
    initialQueryState,
    PaginationState,
    stringifyRequestQuery,
    WithChildren,
    QueryState,
    Response } from '../../../../../_metronic/helpers'
import {getRoles} from './_requests'
import {Role, RolesQueryResponse, PaginationLink} from './_models'
import {useQueryRequest} from './QueryRequestProvider'

const QUERIES = {
  ROLES_LIST: 'roles-list',
}

const QueryResponseContext = createResponseContext<Role>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const itemsPerPage = state.perPage === 10 || state.perPage === 30 || state.perPage === 50 || state.perPage === 100 
    ? state.perPage 
    : 10

  const queryState: QueryState = {
    page: state.page,
    perPage: itemsPerPage as 10 | 30 | 50 | 100,
    items_per_page: itemsPerPage as 10 | 30 | 50 | 100,
    sort: state.sort,
    order: state.order,
    filter: state.filter,
    search: state.search
  }

  const [query, setQuery] = useState<string>(stringifyRequestQuery(queryState))
  const updatedQuery = useMemo(() => stringifyRequestQuery(queryState), [queryState])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.ROLES_LIST}-${query}`,
    () => getRoles(query),
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  // Format the response to match the expected structure
  const formattedResponse: Response<Role[]> = {
    responseData: response?.data || [],
    data: response?.data || [],
    payload: {
      pagination: {
        page: state.page,
        perPage: itemsPerPage as 10 | 30 | 50 | 100,
        items_per_page: itemsPerPage as 10 | 30 | 50 | 100,
        links: generatePaginationLinks(
          state.page,
          response?.payload?.pagination?.total || 0,
          itemsPerPage
        )
      }
    }
  }

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response: formattedResponse, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

// Helper function to generate pagination links
const generatePaginationLinks = (
  currentPage: number,
  totalItems: number,
  itemsPerPage: number
): PaginationLink[] => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const links: PaginationLink[] = [];

  // Previous button
  if (currentPage > 1) {
    links.push({ label: 'Previous', page: currentPage - 1, active: false, url: null });
  }

  // Generate all page numbers
  for (let i = 1; i <= totalPages; i++) {
    links.push({
      label: i.toString(),
      page: i,
      active: i === currentPage,
      url: null
    });
  }

  // Next button
  if (currentPage < totalPages) {
    links.push({ label: 'Next', page: currentPage + 1, active: false, url: null });
  }

  return links;
};

const useQueryResponse = () => {
  const context = useContext(QueryResponseContext)
  if (!context) {
    throw new Error('useQueryResponse must be used within a QueryResponseProvider')
  }
  return context
}

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  return response?.data || []
}

const useQueryResponseLoading = () => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
} 